import React, { createContext, useContext, useState, useEffect, useMemo, ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ProjectsService } from '@/modules/projects/shared/services/projects.service';
import { Project } from '@/modules/projects/shared/types/projects.types';
import { useAuth } from '@/core/auth/AuthContext';

interface ProjectContextType {
  activeProject: Project | null;
  activeProjectId: string | null;
  setActiveProjectId: (id: string) => void;
  projects: Project[];
  isLoading: boolean;
}

const ProjectContext = createContext<ProjectContextType | undefined>(undefined);

export const ProjectProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { profile } = useAuth();
  const [activeProjectId, _setActiveProjectId] = useState<string | null>(() => {
    return localStorage.getItem('landscape_active_project_id');
  });

  const { data, isLoading } = useQuery({
    queryKey: ['projects', 'list-simple'],
    queryFn: () => ProjectsService.getProjects({ page: 1, limit: 100 } as any),
    enabled: !!profile?.company_id,
  });

  const projects = useMemo(() => data?.data || [], [data?.data]);

  useEffect(() => {
    // Nếu chưa có dự án được chọn, mặc định lấy dự án đầu tiên
    if (!activeProjectId && projects.length > 0) {
      const firstId = projects[0].id;
      _setActiveProjectId(firstId);
      localStorage.setItem('landscape_active_project_id', firstId);
    }
  }, [projects, activeProjectId]);

  const setActiveProjectId = (id: string) => {
    _setActiveProjectId(id);
    localStorage.setItem('landscape_active_project_id', id);
  };

  const activeProject = projects.find(p => p.id === activeProjectId) || null;

  return (
    <ProjectContext.Provider value={{
      activeProject,
      activeProjectId,
      setActiveProjectId,
      projects,
      isLoading
    }}>
      {children}
    </ProjectContext.Provider>
  );
};

export const useProjectContext = () => {
  const context = useContext(ProjectContext);
  if (context === undefined) {
    throw new Error('useProjectContext must be used within a ProjectProvider');
  }
  return context;
};
